/* ⚛ REACT */
import { useEffect, useLayoutEffect, useRef, useState } from "react";

/* 📦 LIBS */
import { useTranslation } from "react-i18next";

/* 🎨 STYLES */
import { Container, Field, CountryPrefix, TextInput, StatusIcon, HelperText, SuggestionButton } from "./styles";

/* 🔧 UTILS */
import { COUNTRY_CODE, MAX_DIGITS, formatNational, toNationalDigits, validateBrPhone, toApiPhone } from "../../util/phone";

/**
 * Campo de telefone brasileiro.
 * - O usuário digita só DDD + número; o +55 é fixo
 * - Formata enquanto digita (fixo ou celular), aceita colar em qualquer formato
 * - Valida no blur com mensagens específicas (DDD, dígitos faltando, celular)
 *
 * @param {'phone'|'whatsapp'} kind whatsapp exige celular e envia com o 55
 * @param {string} value valor inicial em qualquer formato (ex.: o que vem da API)
 * @param {Function} onchange (apiValue, isValid) => void — chamado só quando o usuário altera
 * @param {string} suggestion número para o atalho "usar o mesmo número" (opcional)
 * @param {string} suggestionText texto do atalho
 */
const PhoneInput = ({ id, name, kind = 'phone', value, required = false, placeholder, innerRef, onchange, suggestion, suggestionText, aling, width }) => {
    const { t } = useTranslation();
    const mobileOnly = kind === 'whatsapp';

    const inputRef = useRef(null);
    const pendingCaret = useRef(null);
    const dirty = useRef(false);

    const [digits, setDigits] = useState(() => toNationalDigits(value));
    const [focused, setFocused] = useState(false);
    const [touched, setTouched] = useState(false);

    // Atualiza com o valor vindo da API enquanto o usuário não editou
    useEffect(() => {
        if (!dirty.current) setDigits(toNationalDigits(value));
    }, [value]);

    // Reposiciona o cursor depois da formatação
    useLayoutEffect(() => {
        if (pendingCaret.current === null || !inputRef.current) return;
        const formatted = inputRef.current.value;
        let seen = 0;
        let position = formatted.length;
        if (pendingCaret.current === 0) {
            position = formatted.startsWith('(') ? 1 : 0;
        } else {
            for (let i = 0; i < formatted.length; i++) {
                if (/\d/.test(formatted[i])) seen++;
                if (seen === pendingCaret.current) { position = i + 1; break; }
            }
        }
        inputRef.current.setSelectionRange(position, position);
        pendingCaret.current = null;
    }, [digits]);

    const validation = validateBrPhone(digits, { mobileOnly, required });
    // Erro de DDD aparece na hora; o resto só depois que o usuário sai do campo
    const showError = !validation.valid && (touched || validation.error === 'ddd');
    const isComplete = validation.valid && digits.length > 0;

    const commit = (nextDigits, caret = null) => {
        dirty.current = true;
        pendingCaret.current = caret;
        setDigits(nextDigits);
        if (onchange) {
            onchange(toApiPhone(nextDigits, mobileOnly), validateBrPhone(nextDigits, { mobileOnly, required }).valid);
        }
    };

    const handleChange = (e) => {
        const raw = e.target.value;
        const caret = e.target.selectionStart ?? raw.length;
        const rawDigits = raw.replace(/\D/g, '');
        const isTyping = e.nativeEvent?.inputType === 'insertText';

        // Digitando com o número completo: ignora o dígito extra (não confundir DDD 55 com o código do país)
        if (isTyping && rawDigits.replace(/^0+/, '').length > MAX_DIGITS) return;

        // Colar/autopreencher aceita qualquer formato (+55, 0, espaços...)
        const next = isTyping ? rawDigits.replace(/^0+/, '') : toNationalDigits(raw);
        // Colou com +55/0 na frente: os dígitos mudaram de posição, cursor vai para o final
        const digitsBeforeCaret = rawDigits.length === next.length
            ? Math.min(raw.slice(0, caret).replace(/\D/g, '').length, next.length)
            : next.length;
        commit(next, digitsBeforeCaret);
    };

    // Apagar sobre "(", ")", " " ou "-" remove o dígito vizinho em vez de travar
    const handleKeyDown = (e) => {
        if (e.key !== 'Backspace' && e.key !== 'Delete') return;
        const el = e.target;
        if (el.selectionStart !== el.selectionEnd) return;

        const caret = el.selectionStart;
        const charIndex = e.key === 'Backspace' ? caret - 1 : caret;
        const char = el.value[charIndex];
        if (char === undefined || /\d/.test(char)) return;

        e.preventDefault();
        const digitsBefore = el.value.slice(0, caret).replace(/\D/g, '').length;
        if (e.key === 'Backspace' && digitsBefore > 0) {
            commit(digits.slice(0, digitsBefore - 1) + digits.slice(digitsBefore), digitsBefore - 1);
        } else if (e.key === 'Delete' && digitsBefore < digits.length) {
            commit(digits.slice(0, digitsBefore) + digits.slice(digitsBefore + 1), digitsBefore);
        }
    };

    const suggestionDigits = toNationalDigits(suggestion);
    const showSuggestion = suggestionDigits && suggestionDigits !== digits
        && validateBrPhone(suggestionDigits, { mobileOnly }).valid;

    const errorMessage = () => {
        switch (validation.error) {
            case 'required': return t("inputs.phone-field.errors.required");
            case 'ddd': return t("inputs.phone-field.errors.ddd", { ddd: digits.slice(0, 2) });
            case 'incomplete': return t("inputs.phone-field.errors.incomplete", { count: validation.missing });
            case 'mobile': return t("inputs.phone-field.errors.mobile");
            default: return t("inputs.phone-field.errors.invalid");
        }
    };

    const helperId = `${id}-helper`;

    return (
        <Container $aling={aling} $width={width}>
            <Field $focused={focused} $error={showError} $valid={isComplete}>
                <CountryPrefix aria-hidden="true">+{COUNTRY_CODE}</CountryPrefix>
                <TextInput
                    id={id}
                    name={name}
                    ref={(el) => {
                        inputRef.current = el;
                        if (innerRef) innerRef.current = el;
                    }}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel-national"
                    placeholder={placeholder || "(00) 00000-0000"}
                    value={formatNational(digits)}
                    aria-invalid={showError}
                    aria-describedby={helperId}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setFocused(true)}
                    onBlur={() => {
                        setFocused(false);
                        if (digits.length > 0 || dirty.current) setTouched(true);
                    }}
                />
                {isComplete && (
                    <StatusIcon aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none"><path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </StatusIcon>
                )}
            </Field>

            {/* Linha fixa abaixo do campo (mantém o alinhamento com os campos vizinhos):
                erro > atalho "mesmo número" > dica (só telefone; no WhatsApp a do telefone já basta) */}
            <HelperText id={helperId} $error={showError} role={showError ? "alert" : undefined}>
                {showError ? errorMessage()
                    : showSuggestion ? (
                        <SuggestionButton type="button" onClick={() => commit(suggestionDigits.slice(0, MAX_DIGITS))}>
                            {suggestionText || t("inputs.phone-field.same-as-phone")}
                        </SuggestionButton>
                    )
                    : !mobileOnly && t("inputs.phone-field.hint-phone")}
            </HelperText>
        </Container>
    );
};

export default PhoneInput;
